import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import {
  KeyForagesV1,
  SeasonalBaitColorsV1,
  SeasonalPatternsV2,
  SeasonalStructuresV1,
  SeasonGroup,
  Species,
  WaterbodyDetail,
  WaterbodySummary,
} from '@omniafishing/core';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import AnimateHeight from 'react-animate-height';
import aiLogo from '../../../../assets/omnia_ai_logo.png';
import { useProUpsellModal } from '../../../../hooks/use_pro_upsell_modal';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useUser } from '../../../../hooks/use_user';
import { WebAnalytics } from '../../../../lib/web_analytics';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import styles from './lake_summary.less';

export const ProUnlockButton = () => {
  const { openProUpsellModal } = useProUpsellModal();

  return (
    <button
      onClick={openProUpsellModal}
      className="bg-gradient-to-r from-[#002BFF] to-[#FF2A00] text-white font-termina text-[11px] rounded-full px-4 py-2 shadow-lg shadow-black/25"
    >
      Unlock <strong>PRO</strong> Insights w/Free Trial
    </button>
  );
};

export const TableSeasonalPatterns: React.FC<{
  patterns: SeasonalPatternsV2['patterns'];
  species?: Species;
  seasonGroup?: SeasonGroup;
}> = ({ patterns, seasonGroup, species }) => {
  let patternsFiltered = patterns;
  if (species || seasonGroup) {
    patternsFiltered = patternsFiltered.filter((p) => {
      const seasonsDisplayNames = seasonGroup ? seasonGroup.seasons.map((s) => s.display_name) : [];
      if (species && seasonGroup) {
        return p.species === species.display_name && seasonsDisplayNames.includes(p.season);
      }
      if (species) {
        return p.species === species.display_name;
      }
      if (seasonGroup) {
        return seasonsDisplayNames.includes(p.season);
      }
    });
  }

  if (patternsFiltered.length === 0) {
    const filtersText = [species?.display_name, seasonGroup?.display_name]
      .filter(Boolean)
      .join(' and ');
    return <p>No data available for {filtersText}</p>;
  }
  return (
    <>
      <table className={styles.table} width={1770}>
        <thead>
          <tr>
            <th>Season</th>
            <th>Species</th>
            <th>Water Temp</th>
            <th>Behavioral Patterns</th>
            <th>Key Locations</th>
            <th>Best Techniques</th>
            <th>Forage Types</th>
            <th>Bait Colors</th>
          </tr>
        </thead>
        <tbody>
          {patternsFiltered.map((pattern, i) => {
            return (
              <tr key={i}>
                <td width={150}>{pattern.season}</td>
                <td width={150}>{pattern.species}</td>
                <td width={90}>{pattern.water_temp_range_f}</td>
                <td width={330}>
                  <ul className="flex flex-col gap-1">
                    {pattern.behavioral_patterns.map((bp, j) => (
                      <li key={j}>{bp}</li>
                    ))}
                  </ul>
                </td>
                <td width={330}>
                  <ul className="flex flex-col gap-1">
                    {pattern.key_locations.map((kl, j) => (
                      <li key={j}>{kl}</li>
                    ))}
                  </ul>
                </td>
                <td width={330}>
                  <ul className="flex flex-col gap-1">
                    {pattern.fishing_techniques.map((ft, j) => (
                      <li key={j}>{ft}</li>
                    ))}
                  </ul>
                </td>
                <td width={230}>
                  <ul className="flex flex-col gap-1">
                    {pattern.forage_types.map((ft, j) => (
                      <li key={j}>{ft}</li>
                    ))}
                  </ul>
                </td>
                <td width={160}>
                  <ul className="flex flex-col gap-1">
                    {pattern.bait_colors.map((ft, j) => (
                      <li key={j}>{ft}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export const TableSeasonalStructures: React.FC<{
  seasonalStructure: WaterbodySummary['seasonal_structure'];
  species?: Species;
  seasonGroup?: SeasonGroup;
}> = ({ seasonalStructure, seasonGroup, species }) => {
  let seasonStructuresFiltered = seasonalStructure.seasonal_structure;
  if (species || seasonGroup) {
    seasonStructuresFiltered = seasonStructuresFiltered.filter((ss) => {
      const seasonsDisplayNames = seasonGroup ? seasonGroup.seasons.map((s) => s.display_name) : [];
      if (species && seasonGroup) {
        return ss.species === species.display_name && seasonsDisplayNames.includes(ss.season);
      }
      if (species) {
        return ss.species === species.display_name;
      }
      if (seasonGroup) {
        return seasonsDisplayNames.includes(ss.season);
      }
    });
  }

  if (seasonStructuresFiltered.length === 0) {
    const filtersText = [species?.display_name, seasonGroup?.display_name]
      .filter(Boolean)
      .join(' and ');
    return <p>No data available for {filtersText}</p>;
  }
  return (
    <>
      <table className={styles.table} width={620}>
        <thead>
          <tr>
            <th>Season</th>
            <th>Species</th>
            <th>Primary Structure</th>
            <th>Secondary Structure</th>
          </tr>
        </thead>
        <tbody>
          {seasonStructuresFiltered.map((ss, i) => {
            return (
              <tr key={i}>
                <td width={150}>{ss.season}</td>
                <td width={150}>{ss.species}</td>
                <td width={160}>{ss.primary_structure}</td>
                <td width={160}>{ss.secondary_structure}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export const TableKeyForage: React.FC<{
  keyForage: KeyForagesV1;
  species?: Species;
  seasonGroup?: SeasonGroup;
  frosted: boolean;
}> = ({ keyForage, species, seasonGroup, frosted }) => {
  let keyForageFiltered = keyForage.key_forage;
  if (species || seasonGroup) {
    keyForageFiltered = keyForageFiltered.filter((kf) => {
      const seasonsDisplayNames = seasonGroup ? seasonGroup.seasons.map((s) => s.display_name) : [];
      if (species && seasonGroup) {
        return kf.species === species.display_name && seasonsDisplayNames.includes(kf.season);
      }
      if (species) {
        return kf.species === species.display_name;
      }
      if (seasonGroup) {
        return seasonsDisplayNames.includes(kf.season);
      }
    });
  }

  if (keyForageFiltered.length === 0) {
    const filtersText = [species?.display_name, seasonGroup?.display_name]
      .filter(Boolean)
      .join(' and ');
    return <p>No data available for {filtersText}</p>;
  }

  return (
    <div
      className={classNames(styles.tableWrapper, {
        [styles.tableWrapper__frosted]: frosted,
      })}
    >
      <table
        className={classNames(styles.table, {
          [styles.table__frosted]: frosted,
        })}
        width={620}
      >
        <thead>
          <tr>
            <th>Season</th>
            <th>Species</th>
            <th>Primary Forage</th>
            <th>Secondary Forage</th>
          </tr>
        </thead>
        <tbody>
          {keyForageFiltered.map((ss, i) => {
            return (
              <tr key={i}>
                <td width={150}>{ss.season}</td>
                <td width={150}>{ss.species}</td>
                <td width={160}>{ss.primary_forage}</td>
                <td width={160}>{ss.secondary_forage}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {frosted && (
        <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center">
          <ProUnlockButton />
        </div>
      )}
    </div>
  );
};

export const TableSeasonalBaitColors: React.FC<{
  baitColorsBySeason: SeasonalBaitColorsV1;
  species?: Species;
  seasonGroup?: SeasonGroup;
  frosted: boolean;
}> = ({ baitColorsBySeason, species, seasonGroup, frosted }) => {
  let baitColors = baitColorsBySeason.seasonal_bait_colors;
  if (species || seasonGroup) {
    baitColors = baitColors.filter((bc) => {
      const seasonsDisplayNames = seasonGroup ? seasonGroup.seasons.map((s) => s.display_name) : [];
      if (species && seasonGroup) {
        return bc.species === species.display_name && seasonsDisplayNames.includes(bc.season);
      }
      if (species) {
        return bc.species === species.display_name;
      }
      if (seasonGroup) {
        return seasonsDisplayNames.includes(bc.season);
      }
    });
  }

  if (baitColors.length === 0) {
    const filtersText = [species?.display_name, seasonGroup?.display_name]
      .filter(Boolean)
      .join(' and ');
    return <p>No data available for {filtersText}</p>;
  }

  return (
    <div
      className={classNames(styles.tableWrapper, {
        [styles.tableWrapper__frosted]: frosted,
      })}
    >
      <table className={classNames(styles.table, { [styles.table__frosted]: frosted })} width={470}>
        <thead>
          <tr>
            <th>Season</th>
            <th>Species</th>
            <th>Bait Colors</th>
          </tr>
        </thead>
        <tbody>
          {baitColors.map((bc, i) => {
            return (
              <tr key={i}>
                <td width={150}>{bc.season}</td>
                <td width={150}>{bc.species}</td>
                <td width={160}>
                  <ul className="flex flex-col gap-1">
                    {bc.bait_colors.map((c, j) => (
                      <li key={j}>{c}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {frosted && (
        <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center">
          <ProUnlockButton />
        </div>
      )}
    </div>
  );
};

interface LakeSummaryProps {
  waterbody: WaterbodyDetail;
  summary: {
    key_forage: KeyForagesV1 | null;
    seasonal_bait_colors: SeasonalBaitColorsV1 | null;
    seasonal_patterns: SeasonalPatternsV2 | null;
    seasonal_structure: SeasonalStructuresV1 | null;
  };
}

interface LakeSummaryProps {
  waterbody: WaterbodyDetail;
  summary: {
    key_forage: KeyForagesV1 | null;
    seasonal_bait_colors: SeasonalBaitColorsV1 | null;
    seasonal_patterns: SeasonalPatternsV2 | null;
    seasonal_structure: SeasonalStructuresV1 | null;
  };
  selectedSpecies: Species | null;
  selectedSeasonGroup: SeasonGroup | null;
}

export const LakeSummary = (props: LakeSummaryProps) => {
  const { waterbody, summary, selectedSeasonGroup, selectedSpecies } = props;

  const { seasonal_patterns, seasonal_structure, key_forage, seasonal_bait_colors } = summary;

  const { isPro } = useUser();
  const { isDesktop, isMobile } = useResponsive();
  const [height, setHeight] = React.useState<0 | 'auto'>(0);
  const [selectedThemeIndex, setSelectedThemeIndex] = React.useState<number>();

  const themes: {
    name: string;
    value: string;
    pro: boolean;
  }[] = [];

  if (seasonal_patterns) {
    themes.push({
      name: 'Seasonal Patterns',
      value: 'seasonal_patterns',
      pro: false,
    });
  }
  if (seasonal_structure) {
    themes.push({
      name: 'Seasonal Structure',
      value: 'seasonal_structure',
      pro: false,
    });
  }
  if (key_forage) {
    themes.push({
      name: 'Key Forage',
      value: 'key_forage',
      pro: true,
    });
  }
  if (seasonal_bait_colors) {
    themes.push({
      name: 'Bait Colors by Season',
      value: 'seasonal_bait_colors',
      pro: true,
    });
  }

  const selectedTheme = themes[selectedThemeIndex];

  useEffect(() => {
    WebAnalytics.lakeAiSummaryRendered({
      waterbody_id: waterbody.id,
      location: 'overview',
    });
  }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div>
          <p className="font-bold text-base">Local Fishing Reports Summary</p>
          <p className="text-black/50 text-xs">
            From {waterbody.fishing_reports_count} Real Fishing Reports + Weather
          </p>
        </div>
        <div className="flex-grow-0 flex-shrink-0">
          <img src={aiLogo} alt="Omnia AI" width={35} className="hidden sm:block" />
        </div>
      </div>

      <ol
        className={`flex gap-2 mb-3 items-start ${isMobile && 'flex-col'} ${
          isDesktop && 'flex-row flex-wrap'
        }`}
      >
        {themes.map((theme, i) => {
          const selected = selectedTheme?.value === theme.value && height === 'auto';
          return (
            <li
              key={theme.name}
              className={`relative rounded-full p-1 border-1 border-black/15 shadow select-none bg-white cursor-pointer text-[13px] bg-clip-padding hover:bg-sky-50 hover:-translate-y-0.5 hover:shadow-md transition-all ${
                selected ? 'bg-gradient-to-r from-[#002BFF] to-[#FF2A00] border-transparent' : ''
              }`}
              onClick={() => {
                if (selected) {
                  setHeight(0);
                } else {
                  setHeight('auto');
                }
                setSelectedThemeIndex(i);
                WebAnalytics.lakeAiSummaryThemeClicked({
                  theme: theme.name,
                  waterbody_id: waterbody.id,
                  location: 'overview',
                });
              }}
            >
              <span
                className={`py-1 px-4 rounded-full font-bold flex gap-2 ${
                  selected ? 'bg-black text-white' : ''
                }`}
              >
                {theme.name}
                <RightOutlined rotate={selected ? 90 : 0} />
              </span>
              {theme.pro && (
                <span
                  className={`rounded-full text-[8px] font-termina px-1 py-0.5 bg-[#002BFF] text-white absolute bottom-0 right-0 translate-y-1/2 shadow-md shadow-black/25`}
                >
                  PRO
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <AnimateHeight height={height} easing="ease-out" duration={200}>
        <div className="bg-gradient-to-b from-[#002BFF] to-[#FF2A00] rounded-[12px] p-[1px]">
          {selectedTheme && (
            <div className={`p-3 relative rounded-[11px] bg-white`}>
              <p className="mb-2 mr-7 text-[15px] font-termina font-bold">{selectedTheme.name}</p>
              <div className="-mx-3 px-3 overflow-auto">
                {selectedTheme.value === 'seasonal_patterns' && (
                  <TableSeasonalPatterns
                    patterns={seasonal_patterns.patterns}
                    seasonGroup={selectedSeasonGroup}
                    species={selectedSpecies}
                  />
                )}
                {selectedTheme.value === 'seasonal_structure' && (
                  <TableSeasonalStructures
                    seasonalStructure={summary.seasonal_structure}
                    seasonGroup={selectedSeasonGroup}
                    species={selectedSpecies}
                  />
                )}
                {selectedTheme.value === 'key_forage' && (
                  <TableKeyForage
                    keyForage={summary.key_forage}
                    seasonGroup={selectedSeasonGroup}
                    species={selectedSpecies}
                    frosted={!isPro}
                  />
                )}
                {selectedTheme.value === 'seasonal_bait_colors' && (
                  <TableSeasonalBaitColors
                    baitColorsBySeason={summary.seasonal_bait_colors}
                    seasonGroup={selectedSeasonGroup}
                    species={selectedSpecies}
                    frosted={!isPro}
                  />
                )}
              </div>
              <OmniaButton
                size="sm"
                shape="circle"
                kind="tertiary"
                className="absolute top-3 right-3"
                onClick={() => {
                  setHeight(0);
                }}
              >
                <CloseOutlined className="flex-grow-0 flex-shrink-0" />
              </OmniaButton>
            </div>
          )}
        </div>
      </AnimateHeight>
    </div>
  );
};
